import React, { useCallback, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { type Job } from '@/types/jobs';

import { JobCreateHeader } from '@/pages/jobs/forms/JobCreateHeader';
import { JobGeneralInfo } from '@/pages/jobs/forms/JobGeneralInfo';
import { JobInsuranceInfo } from '@/pages/jobs/forms/JobInsuranceInfo';
import { JobAssignmentsForm } from '@/pages/jobs/forms/JobAssignments';
import { JobNotes } from '@/pages/jobs/forms/JobNotes';

import { useDialog } from '@/contexts/DialogContext';
import { confirmDiscardUnsavedChanges } from '@/hooks/useUnsavedChangesGuard';
import { useJobCreateFormState } from '@/pages/jobs/hooks/useJobCreateFormState';
import { useJobClassificationEdit } from '@/pages/jobs/hooks/useJobClassificationEdit';
import { useJobCreateSubmit } from '@/pages/jobs/hooks/useJobCreateSubmit';

interface JobCreateProps {
    onClose: () => void;
    initialData?: Job;
    jobId?: string;
}

export const JobCreate: React.FC<JobCreateProps> = ({ onClose, initialData, jobId }) => {
    const form = useJobCreateFormState({ initialData });
    const { requestClassificationChange, confirmClassificationChange, buildClassification } =
        useJobClassificationEdit(form);
    const { handleSubmit, loading, submitError } = useJobCreateSubmit({
        form,
        buildClassification,
        onClose,
        initialData,
        jobId,
    });

    const { confirm } = useDialog();
    const { isDirty } = form;
    const closingRef = useRef(false);

    // Every dismissal path (backdrop, header X, Cancel, Escape) funnels through
    // here: a form with unsaved input is only discarded after a confirm that
    // names what is lost (audit H10a / PR-5 - a 20-plus-field FNOL entry lost
    // to one stray click). A clean form closes unobstructed. closingRef stops
    // a repeat backdrop click / Escape from stacking a second confirm.
    // Successful saves close through onClose directly (nothing is lost there).
    const requestClose = useCallback(async () => {
        if (closingRef.current) return;
        closingRef.current = true;
        try {
            const ok = await confirmDiscardUnsavedChanges(
                isDirty(),
                confirm,
                jobId
                    ? 'Discard your unsaved changes to this job? The edits you made to the job details will be lost. Previously saved job data is not affected.'
                    : 'Discard this job? It has not been created, and everything entered on the form (customer, property, insurance, loss details, and assignments) will be lost.',
                {
                    title: jobId ? 'Discard unsaved changes?' : 'Discard this job?',
                    confirmLabel: jobId ? 'Discard changes' : 'Discard job',
                    cancelLabel: 'Keep editing',
                },
            );
            if (ok) onClose();
        } finally {
            closingRef.current = false;
        }
    }, [confirm, isDirty, jobId, onClose]);

    useEffect(() => {
        const handleKeyDown = (event: KeyboardEvent) => {
            if (event.key === 'Escape') {
                void requestClose();
            }
        };
        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [requestClose]);

    const {
        profile,
        orgUsers,
        offices,
        departments,
        availableUsers,
        officeId, setOfficeId,
        departmentId, setDepartmentId,
        customerName, setCustomerName,
        customerPhone, setCustomerPhone,
        lossDate, setLossDate,
        fnolReceivedDate, setFnolReceivedDate,
        lossCategory, setLossCategory,
        carrier, setCarrier,
        claimNumber, setClaimNumber,
        policyNumber, setPolicyNumber,
        adjusterName, setAdjusterName,
        adjusterEmail, setAdjusterEmail,
        adjusterPhone, setAdjusterPhone,
        lossDescription, setLossDescription,
        notes, setNotes,
        address, setAddress,
        city, setCity,
        state, setState,
        zip, setZip,
        county, setCounty,
        iicrcCategory,
        iicrcClass,
        pendingClassificationChange, setPendingClassificationChange,
        assignments, setAssignments,
    } = form;

    // WEB-114 increment 4 (product doctrine rules 8 and 12): Create / Save is visible without scrolling at every width.
    // The modal is capped to the viewport and only the form body scrolls, so the footer stays on screen. Below xl the
    // whole overlay used to scroll, which put the footer after every field, about 2,400 px down a phone.
    return createPortal(
        <div
            className="fixed inset-0 bg-bg-primary/90 flex items-start xl:items-center justify-center z-[3000] backdrop-blur-md p-2 md:p-8 overflow-hidden"
            onClick={() => { void requestClose(); }}
        >
            <div
                data-testid="job-form-modal"
                className="glass w-full max-w-7xl relative border border-divider-subtle shadow-2xl animate-in fade-in zoom-in duration-300 flex flex-col max-h-full my-auto"
                onClick={(event) => event.stopPropagation()}
            >

                <JobCreateHeader isEditMode={!!jobId} onClose={() => { void requestClose(); }} />

                <form id="job-create-form" onSubmit={handleSubmit} data-testid="job-form-body" className="flex-1 min-h-0 overflow-y-auto overflow-x-hidden p-4 md:p-6">

                    <div className="grid grid-cols-1 xl:grid-cols-12 gap-6 items-start xl:items-stretch xl:h-full xl:min-h-0">
                        <div className="xl:col-span-3 space-y-6 xl:space-y-0 xl:flex xl:flex-col xl:min-h-0 xl:gap-6">
                            <section className="glass rounded-2xl border border-divider-subtle p-5">
                                    <div className="flex items-center justify-between mb-3">
                                        <h3 className="text-sm font-black uppercase tracking-widest text-text-muted">{initialData ? 'Job Snapshot' : 'Job Context'}</h3>
                                        {initialData && (
                                            <span className="text-micro uppercase px-2 py-1 rounded-full bg-surface-active text-text-primary font-bold">{initialData.status}</span>
                                        )}
                                    </div>
                                    <div className="grid grid-cols-2 gap-3 mb-4">
                                        <div className="space-y-1">
                                            <label className="text-micro font-bold text-text-muted uppercase">Office</label>
                                            <select
                                                value={officeId}
                                                onChange={(e) => setOfficeId(e.target.value)}
                                                required
                                                disabled={Boolean(profile?.officeId)}
                                                className={`input-field appearance-none ${profile?.officeId ? 'opacity-50 cursor-not-allowed' : ''}`}
                                            >
                                                <option value="" className="bg-bg-tertiary">Select...</option>
                                                {offices.map(o => <option key={o.id} value={o.id} className="bg-bg-tertiary">{o.name}</option>)}
                                            </select>
                                        </div>
                                        <div className="space-y-1">
                                            <label className="text-micro font-bold text-text-muted uppercase">Department</label>
                                            <select
                                                value={departmentId}
                                                onChange={(e) => setDepartmentId(e.target.value)}
                                                required
                                                disabled={!officeId || !!profile?.departmentId}
                                                className={`input-field appearance-none ${(!officeId || !!profile?.departmentId) ? 'opacity-50 cursor-not-allowed' : ''}`}
                                            >
                                                <option value="" className="bg-bg-tertiary">Select...</option>
                                                {departments.map(d => <option key={d.id} value={d.id} className="bg-bg-tertiary">{d.name}</option>)}
                                            </select>
                                        </div>
                                    </div>
                                    {initialData && (
                                    <div className="space-y-2 text-xs text-text-secondary">
                                        <div className="flex justify-between gap-3">
                                            <span className="text-text-muted">Job ID</span>
                                            <span className="font-mono text-text-primary truncate">{initialData.id}</span>
                                        </div>
                                        <div className="flex justify-between gap-3">
                                            <span className="text-text-muted">Current Owner Dept</span>
                                            <span className="text-text-primary truncate">{initialData.departmentId || 'N/A'}</span>
                                        </div>
                                        <div className="flex justify-between gap-3">
                                            <span className="text-text-muted">Phases</span>
                                            <span className="text-text-primary">{initialData.phases?.length ?? 0}</span>
                                        </div>
                                        <div className="flex justify-between gap-3">
                                            <span className="text-text-muted">Created</span>
                                            <span className="text-text-primary">{initialData.createdAt?.toDate?.().toLocaleDateString?.() || 'N/A'}</span>
                                        </div>
                                        <div className="flex justify-between gap-3">
                                            <span className="text-text-muted">Updated</span>
                                            <span className="text-text-primary">{initialData.updatedAt?.toDate?.().toLocaleDateString?.() || 'N/A'}</span>
                                        </div>
                                    </div>
                                    )}
                                </section>

                            <section className="glass rounded-2xl border border-divider-subtle p-5 xl:flex-1 xl:min-h-0 xl:flex xl:flex-col">
                                <JobNotes
                                    lossDescription={lossDescription}
                                    setLossDescription={setLossDescription}
                                    notes={notes}
                                    setNotes={setNotes}
                                    embedded
                                    fillHeight
                                />
                            </section>
                        </div>

                        <section className="xl:col-span-5 glass rounded-2xl border border-divider-subtle p-5 xl:h-full xl:min-h-0 xl:overflow-y-auto xl:custom-scrollbar">
                            <JobInsuranceInfo
                                lossDate={lossDate} setLossDate={setLossDate}
                                lossCategory={lossCategory} setLossCategory={setLossCategory}
                                carrier={carrier} setCarrier={setCarrier}
                                claimNumber={claimNumber} setClaimNumber={setClaimNumber}
                                policyNumber={policyNumber} setPolicyNumber={setPolicyNumber}
                                adjusterName={adjusterName} setAdjusterName={setAdjusterName}
                                adjusterEmail={adjusterEmail} setAdjusterEmail={setAdjusterEmail}
                                adjusterPhone={adjusterPhone} setAdjusterPhone={setAdjusterPhone}
                                iicrcCategory={iicrcCategory} setIicrcCategory={(value) => requestClassificationChange('category', value)}
                                iicrcClass={iicrcClass} setIicrcClass={(value) => requestClassificationChange('class', value)}
                            />
                        </section>

                        <div className="xl:col-span-4 xl:flex xl:flex-col xl:min-h-0 xl:h-full xl:gap-6">
                            <section className="glass rounded-2xl border border-divider-subtle p-5">
                                <JobGeneralInfo
                                    customerName={customerName} setCustomerName={setCustomerName}
                                    customerPhone={customerPhone} setCustomerPhone={setCustomerPhone}
                                    address={address} setAddress={setAddress}
                                    city={city} setCity={setCity}
                                    state={state} setState={setState}
                                    zip={zip} setZip={setZip}
                                    county={county} setCounty={setCounty}
                                    fnolReceivedDate={fnolReceivedDate} setFnolReceivedDate={setFnolReceivedDate}
                                />
                            </section>

                            <section className="glass rounded-2xl border border-divider-subtle p-5 xl:flex-1 xl:min-h-0">
                                <JobAssignmentsForm
                                    assignments={assignments}
                                    setAssignments={setAssignments}
                                    orgUsers={orgUsers}
                                    availableUsers={availableUsers}
                                />
                            </section>
                        </div>
                    </div>

                </form>

                {/* Footer Actions */}
                <footer data-testid="job-form-footer" className="p-3 md:p-6 border-t border-divider-subtle flex items-center gap-3 md:gap-4 flex-none bg-surface-elevation-1">
                    {submitError && (
                        <div className="flex-1 text-red-400 text-sm font-medium" role="alert">
                            {submitError}
                        </div>
                    )}
                    <button
                        type="button"
                        onClick={() => { void requestClose(); }}
                        className="ml-auto px-6 py-3 rounded-xl bg-transparent border border-divider-subtle text-text-primary font-bold hover:bg-surface-hover transition-all"
                    >
                        Cancel
                    </button>
                    <button
                        type="submit"
                        form="job-create-form"
                        disabled={loading}
                        className="px-8 py-3 rounded-xl bg-accent-electric text-black font-bold shadow-[0_0_20px_rgb(var(--accent-electric-rgb)/0.2)] hover:shadow-[0_0_30px_rgb(var(--accent-electric-rgb)/0.4)] hover:bg-text-primary hover:text-bg-primary transition-all transform hover:-translate-y-0.5 active:translate-y-0 disabled:opacity-50 disabled:transform-none"
                    >
                        {loading ? 'Saving...' : (jobId ? 'Save Changes' : 'Create Job Project')}
                    </button>
                </footer>

                {pendingClassificationChange && (
                    <div className="absolute inset-0 z-40 bg-bg-primary/75 backdrop-blur-sm flex items-center justify-center p-4">
                        <div className="glass-premium w-full max-w-md p-5 electric-glow">
                            <h3 className="text-sm font-black uppercase tracking-widest text-accent-electric mb-2">
                                Confirm Classification Change
                            </h3>
                            <p className="text-sm text-text-secondary leading-relaxed mb-4">
                                Changing the IICRC {pendingClassificationChange.field} will clear stored AI reasoning
                                for loss classification in this job document. Continue?
                            </p>
                            <div className="flex justify-end gap-3">
                                <button
                                    type="button"
                                    onClick={() => setPendingClassificationChange(null)}
                                    className="px-4 py-2 rounded-lg border border-divider-subtle text-text-secondary hover:text-text-primary hover:bg-surface-hover transition-colors"
                                >
                                    Cancel
                                </button>
                                <button
                                    type="button"
                                    onClick={confirmClassificationChange}
                                    className="px-4 py-2 rounded-lg bg-accent-electric text-black font-bold hover:bg-text-primary hover:text-bg-primary transition-colors"
                                >
                                    Confirm
                                </button>
                            </div>
                        </div>
                    </div>
                )}

            </div>
        </div>,
        document.body
    );
};
